import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  GoogleSignInRequest,
  GoogleSignInResponse,
  RecruitTokenRequest,
  RecruitTokenResponse,
  VettaUser,
} from '../models/auth.model';
import { DEFAULT_RETURN_URL, ParkedSignIn, UNRECOGNISED_REFUSAL, refusalMessage, resolveCallback, startSignIn, tokenIsUsable } from '../util/sign-in';

/** The recruit JWT and the display profile. Nothing else resembling a credential is stored. */
interface StoredAuth {
  token: string | null;
  profile: VettaUser;
}

const NOT_CONFIGURED = 'This build has no Google client ID, so sign-in cannot start.';
const SESSION_EXPIRED = 'Your session expired. Sign in again.';

const AUTH_KEY = 'vetta.auth';
const PARKED_KEY = 'vetta.sign-in';

/** The contract declares no error body, so the status stands in when no `slug` is sent. */
const GOOGLE_REFUSAL: Record<number, string> = { 400: 'invalid-request', 401: 'google-auth-failed', 403: 'not-an-employee' };
const TOKEN_REFUSAL: Record<number, string> = { 400: 'invalid-request', 401: 'session-unusable' };

/**
 * The I/O shell around `util/sign-in`: HTTP, storage and navigation, and no decisions. The two
 * auth calls use `HttpClient` directly rather than the RecruitApi seam — see decision 0001.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly router = inject(Router);
  private readonly http = inject(HttpClient);
  private jwt: string | null = null;
  private returnUrl: string | null = null;

  readonly user = signal<VettaUser | null>(null);
  readonly error = signal<string | null>(null);
  readonly isSignedIn = computed(() => this.user() !== null);

  constructor() {
    const stored = read<StoredAuth>(localStorage, AUTH_KEY);
    if (!stored) return;
    // Only the mock stand-in stores an explicit null; anything else has to stand up to `exp`.
    if (stored.token !== null && !tokenIsUsable(stored.token)) {
      localStorage.removeItem(AUTH_KEY);
      return;
    }
    this.jwt = stored.token;
    this.user.set(stored.profile);
  }

  /** Null in mock mode, where the stand-in recruiter has no credential. */
  token(): string | null {
    return this.jwt;
  }

  /** Where the guard turned the recruiter away, for sign-in to return them to. */
  rememberReturnUrl(url: string): void {
    this.returnUrl = url;
  }

  /** Mock mode only: no Google, no backend, no token. */
  signInWithMock(): void {
    this.complete(null, { email: 'dhanush@' + environment.allowedDomain, name: 'Dhanush' }, this.returnUrl ?? DEFAULT_RETURN_URL);
  }

  /** Parks the verifier, state and return URL, then sends the whole tab to Google. */
  async signInWithGoogle(): Promise<void> {
    this.error.set(null);
    if (!environment.googleClientId) return this.error.set(NOT_CONFIGURED);
    const config = { origin: location.origin, clientId: environment.googleClientId, allowedDomain: environment.allowedDomain };
    const { authorizeUrl, parked } = await startSignIn(config, this.returnUrl);
    sessionStorage.setItem(PARKED_KEY, JSON.stringify(parked));
    location.assign(authorizeUrl);
  }

  /**
   * Resolves what Google returned, exchanges the code for the employee and the employee's ID
   * token for the recruit JWT, and discards the ID token. Never throws to the caller: every
   * failure lands back on the sign-in page with a message.
   */
  async completeSignIn(params: Record<string, string | undefined>): Promise<void> {
    const parked = read<ParkedSignIn>(sessionStorage, PARKED_KEY);
    sessionStorage.removeItem(PARKED_KEY);
    // This page load wiped the in-memory copy, so a retry after a refusal still has one.
    if (parked) this.returnUrl = parked.returnUrl;

    const result = resolveCallback(params, parked, environment.allowedDomain);
    if (result.kind === 'refused') return this.refuse(result.message);

    try {
      const body: GoogleSignInRequest = { code: result.code, verifier: result.verifier };
      const signedIn = await this.post<GoogleSignInResponse>('/auth/google', body, GOOGLE_REFUSAL);
      const exchange: RecruitTokenRequest = { googleToken: signedIn.user.idToken };
      const { token } = await this.post<RecruitTokenResponse>('/auth/token', exchange, TOKEN_REFUSAL);
      const { email, name, photo } = signedIn.user;
      this.complete(token, { email, name, picture: photo || undefined }, result.returnUrl);
    } catch (e) {
      this.refuse(e instanceof Error ? e.message : UNRECOGNISED_REFUSAL);
    }
  }

  /** A 401 from any Recruit Service call: the token is dead, whatever `exp` said. */
  expire(): void {
    this.signOut();
    this.error.set(SESSION_EXPIRED);
  }

  signOut(): void {
    localStorage.removeItem(AUTH_KEY);
    this.jwt = null;
    this.returnUrl = null;
    this.user.set(null);
    this.router.navigateByUrl('/sign-in');
  }

  private async post<T>(path: string, body: unknown, byStatus: Record<number, string>): Promise<T> {
    try {
      return await firstValueFrom(this.http.post<T>(environment.apiBaseUrl + path, body));
    } catch (e) {
      const slug = (e instanceof HttpErrorResponse && ((e.error as { slug?: string } | null)?.slug ?? byStatus[e.status])) || '';
      throw new Error(refusalMessage(slug, environment.allowedDomain));
    }
  }

  private complete(token: string | null, profile: VettaUser, returnUrl: string): void {
    this.error.set(null);
    this.returnUrl = null;
    this.jwt = token;
    this.user.set(profile);
    localStorage.setItem(AUTH_KEY, JSON.stringify({ token, profile } satisfies StoredAuth));
    this.router.navigateByUrl(returnUrl, { replaceUrl: true });
  }

  /** Replaces the callback URL so a refresh cannot resubmit a spent code. */
  private refuse(message: string): void {
    this.error.set(message);
    this.router.navigateByUrl('/sign-in', { replaceUrl: true });
  }
}

function read<T>(store: Storage, key: string): T | null {
  const raw = store.getItem(key);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    store.removeItem(key);
    return null;
  }
}
